import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { EsignPages } from "@/components/EsignPages";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import { CheckCircle2, FileSignature } from "lucide-react";

export default function PublicEsign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [values, setValues] = useState({});
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [padFor, setPadFor] = useState(null);
  const padRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get(`/public/esign/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  if (err) return <Shell><div className="text-center text-rose-600">{err}</div></Shell>;
  if (!info) return <Shell><div className="text-center text-slate-400">Loading…</div></Shell>;
  if (done || info.already_signed) return <Shell><div className="text-center"><CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" /><h2 className="font-head text-xl font-bold text-slate-900">Thank you, {info.recipient?.name}!</h2><p className="text-slate-500 mt-1">Your signature has been recorded.</p></div></Shell>;
  if (info.voided) return <Shell><div className="text-center text-slate-500">This request has been voided.</div></Shell>;
  if (!info.your_turn) return <Shell><div className="text-center text-slate-500">This document is awaiting an earlier signer. You will be emailed when it is your turn.</div></Shell>;

  const color = info.recipient?.color || "#1479C4";
  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));

  const openPad = (f) => { setPadFor(f); };
  const savePad = () => {
    if (!padRef.current || padRef.current.isEmpty()) return toast.error("Please draw first");
    setVal(padFor.id, padRef.current.toDataURL());
    setPadFor(null);
  };

  const submit = async () => {
    if (!agree) return toast.error("Please tick the consent box");
    for (const f of info.fields) {
      if (!f.required) continue;
      if (f.type === "date") continue;
      if (!values[f.id]) return toast.error("Please complete all required fields");
    }
    setSubmitting(true);
    try { await api.post(`/public/esign/${token}/submit`, { agree: true, values }); setDone(true); }
    catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const overlay = (pageIdx) => info.fields.filter((f) => f.page === pageIdx).map((f) => {
    const filled = !!values[f.id] || f.type === "date";
    return (
      <div key={f.id} data-testid={`sign-field-${f.id}`}
        className="absolute rounded flex items-center justify-center pointer-events-auto overflow-hidden"
        style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
          background: filled ? "#ffffff" : `${color}22`, border: `1.5px solid ${color}` }}>
        {f.type === "date" ? (
          <span className="text-[10px] text-slate-700">{today}</span>
        ) : f.type === "text" ? (
          <input data-testid={`sign-text-${f.id}`} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} placeholder="Type here" className="w-full h-full text-[11px] px-1 bg-transparent outline-none" />
        ) : values[f.id] ? (
          <img src={values[f.id]} alt="" className="max-h-full max-w-full object-contain" onClick={() => openPad(f)} />
        ) : (
          <button data-testid={`sign-draw-${f.id}`} onClick={() => openPad(f)} className="text-[10px] font-semibold" style={{ color }}>{f.type === "initials" ? "Initials" : "Sign"}</button>
        )}
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0"><FileSignature className="h-5 w-5 text-primary shrink-0" /><div className="min-w-0"><div className="font-head font-bold text-slate-900 truncate">{info.title}</div><div className="text-xs text-slate-500 truncate">{info.company}</div></div></div>
        <label className="hidden sm:flex items-center gap-2 text-xs text-slate-600"><input data-testid="esign-consent" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> My electronic signature is the legal equivalent of my handwritten signature.</label>
        <button data-testid="esign-submit" onClick={submit} disabled={submitting} className="bg-primary text-white px-5 py-2 rounded-xl font-semibold text-sm disabled:opacity-60 shrink-0">{submitting ? "Submitting…" : "Submit"}</button>
      </div>
      <label className="sm:hidden flex items-center gap-2 text-xs text-slate-600 bg-white px-4 py-2 border-b"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> My e-signature is legally binding.</label>
      {info.message && <div className="max-w-3xl mx-auto px-4 pt-4 text-sm text-slate-600">{info.message}</div>}
      <div className="py-6 px-2">
        <EsignPages pdfUrl={`/public/esign/${token}/pdf`} width={Math.min(760, (typeof window !== "undefined" ? window.innerWidth : 760) - 24)} renderOverlay={(idx) => overlay(idx)} />
      </div>

      {padFor && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4" onClick={() => setPadFor(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head font-bold text-slate-900 mb-2">{padFor.type === "initials" ? "Draw your initials" : "Draw your signature"}</h3>
            <SignaturePad ref={padRef} />
            <div className="flex gap-2 mt-3">
              <button onClick={() => padRef.current?.clear()} className="flex-1 border border-slate-200 rounded-xl py-2 text-sm font-semibold">Clear</button>
              <button data-testid="pad-save" onClick={savePad} className="flex-1 bg-primary text-white rounded-xl py-2 text-sm font-semibold">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4"><div className="bg-white rounded-2xl shadow p-8 max-w-md w-full">{children}</div></div>;
}
